// Tenant-scoped operational data layer. Live mode persists to Supabase
// (app_entities JSONB + RLS + Realtime); mock mode uses localStorage. Same
// synchronous API either way (in-memory cache hydrated from Supabase).
import { useEffect, useState } from "react";
import { supabase, IS_MOCK } from "@/lib/supabaseClient";

const KEY = "bo_data_v6";

export const DEPARTMENTS = [
  { slug: "kok", name: "Kök", icon: "ChefHat", color: "#E15B2D", description: "Mise en place, matsäkerhet och köksrutiner." },
  { slug: "servering", name: "Servering", icon: "UtensilsCrossed", color: "#229954", description: "Gästbemötande, bordsrutiner och service." },
  { slug: "prep", name: "Prep", icon: "Soup", color: "#C58E1A", description: "Förberedelser och dagsprep." },
  { slug: "stad", name: "Disk & Städ", icon: "Sparkles", color: "#2980B9", description: "Egenkontroll, städscheman och HACCP." },
  { slug: "kassa", name: "Kassa", icon: "Wallet", color: "#8E44AD", description: "Dagsavslut och kassahantering." },
  { slug: "lager", name: "Lager", icon: "Package", color: "#566573", description: "Beställningar, inventering och leveranser." },
  { slug: "kunskap", name: "Kunskap", icon: "BookOpen", color: "#4F6BED", description: "Manualer, policys och rutiner samlade på ett ställe." },
  { slug: "foodcost", name: "Ekonomi", icon: "BarChart3", color: "#1F9C6B", description: "Aktuella råvarupriser, recept och exakt matkostnad." },
];
// Departments that are knowledge bases (manuals & policies only, no daily ops).
export const KNOWLEDGE_SLUGS = ["kunskap"];
// Departments only the owner may see/enter.
export const OWNER_ONLY_SLUGS = ["foodcost"];
// Units for foodcost.
export const UNITS = ["kg", "g", "l", "ml", "st"];
const UNIT_FAMILY = { kg: "vikt", g: "vikt", l: "volym", ml: "volym", st: "st" };
const BASE_UNIT = { vikt: "g", volym: "ml", st: "st" };
const TO_BASE = { kg: 1000, g: 1, l: 1000, ml: 1, st: 1 };
export function toBase(qty, unit) { return (Number(qty) || 0) * (TO_BASE[unit] || 1); }
export function baseUnitOf(unit) { return BASE_UNIT[UNIT_FAMILY[unit] || "st"]; }
export function pricePerBase(packPrice, packSize, packUnit) {
  const base = toBase(packSize, packUnit);
  return base > 0 ? (Number(packPrice) || 0) / base : 0;
}
export const departmentBySlug = (s) => DEPARTMENTS.find((d) => d.slug === s);

const uid = () => Math.random().toString(36).slice(2, 10);
const nowISO = () => new Date().toISOString();
const todayISO = () => new Date().toISOString().slice(0, 10);

let listeners = [];
const emit = () => listeners.forEach((l) => l());
export function subscribe(fn) { listeners.push(fn); return () => { listeners = listeners.filter((l) => l !== fn); }; }
export function useStore() {
  const [, setV] = useState(0);
  useEffect(() => subscribe(() => setV((v) => v + 1)), []);
}

const COLLECTIONS = ["checklists", "checklist_runs", "tasks", "incidents", "manuals", "ingredients", "recipes", "invoices", "price_impacts", "audit", "notifications", "settings"];
const emptyBucket = () => Object.fromEntries(COLLECTIONS.map((c) => [c, []]));

// In-memory cache for LIVE (Supabase) mode. localStorage is used only in mock mode.
let memStore = {};

function read() {
  if (IS_MOCK) { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (_) { return {}; } }
  return memStore;
}
function write(db) {
  if (IS_MOCK) { localStorage.setItem(KEY, JSON.stringify(db)); emit(); return; }
  memStore = db; emit(); syncToSupabase(db);
}
function bucket(db, rid) {
  if (!db[rid]) db[rid] = emptyBucket();
  COLLECTIONS.forEach((c) => { if (!db[rid][c]) db[rid][c] = []; });
  return db[rid];
}

// ---------- Supabase write-through (debounced batch upsert) ----------
let syncTimer = null;
const dirtyRids = new Set();
function incidentDocToRow(rid, d) {
  return {
    id: d.id, restaurant_id: rid, department: d.department || null, title: d.title || null,
    description: d.description || null, severity: d.severity || null, status: d.status || null,
    reporter: d.reporter || null, created_by: d.created_by || null,
    created_at: d.created_at || null, updated_at: d.updated_at || null,
    resolved_at: d.resolved_at || null, deleted_at: d.deleted_at || null, data: d,
  };
}

function syncToSupabase(db) {
  Object.keys(db).forEach((rid) => dirtyRids.add(rid));
  if (syncTimer) return;
  syncTimer = setTimeout(async () => {
    const rids = [...dirtyRids]; dirtyRids.clear(); syncTimer = null;
    for (const rid of rids) {
      const b = db[rid]; if (!b) continue;
      const rows = [];
      COLLECTIONS.forEach((c) => {
        if (c === "incidents") return; // normalized table, handled below
        (b[c] || []).forEach((doc) => {
          if (doc && doc.id) rows.push({ id: doc.id, restaurant_id: rid, collection: c, data: doc, deleted_at: doc.deleted_at || null });
        });
      });
      if (rows.length) {
        const { error } = await supabase.from("app_entities").upsert(rows, { onConflict: "id" });
        if (error) console.error("[Restaurangkollen] sync error:", error.message);
      }
      const incRows = (b.incidents || []).filter((d) => d && d.id).map((d) => incidentDocToRow(rid, d));
      if (incRows.length) {
        const { error } = await supabase.from("incidents").upsert(incRows, { onConflict: "id" });
        if (error) console.error("[Restaurangkollen] incidents sync error:", error.message);
      }
    }
  }, 250);
}

// ---------- Supabase hydration + realtime ----------
const hydrated = new Set();
let channel = null, channelRid = null;

export async function hydrate(rid) {
  if (!rid || IS_MOCK) return;
  const [aeRes, incRes] = await Promise.all([
    supabase.from("app_entities").select("id,collection,data").eq("restaurant_id", rid),
    supabase.from("incidents").select("data").eq("restaurant_id", rid),
  ]);
  if (aeRes.error) { console.error("[Restaurangkollen] hydrate error:", aeRes.error.message); return; }
  const b = emptyBucket();
  (aeRes.data || []).forEach((r) => { if (b[r.collection]) b[r.collection].push(r.data); });
  (incRes.data || []).forEach((r) => { if (r.data) b.incidents.push(r.data); });
  COLLECTIONS.forEach((c) => b[c].sort((a, z) => String(z.created_at || "").localeCompare(String(a.created_at || ""))));
  memStore[rid] = b;
  emit();
  if ((aeRes.data || []).length === 0 && (incRes.data || []).length === 0) seedLive(rid);
  subscribeRealtime(rid);
  hydrated.add(rid);
}

function seedLive(rid) {
  const b = emptyBucket();
  seedRestaurant(b, rid);
  memStore[rid] = b;
  emit();
  syncToSupabase(memStore);
}

function subscribeRealtime(rid) {
  if (channelRid === rid && channel) return;
  if (channel) { supabase.removeChannel(channel); channel = null; }
  channelRid = rid;
  channel = supabase.channel("rk-" + rid)
    .on("postgres_changes", { event: "*", schema: "public", table: "app_entities", filter: `restaurant_id=eq.${rid}` },
      (payload) => applyRealtime(rid, payload, null))
    .on("postgres_changes", { event: "*", schema: "public", table: "incidents", filter: `restaurant_id=eq.${rid}` },
      (payload) => applyRealtime(rid, payload, "incidents"))
    .subscribe();
}

function applyRealtime(rid, payload, forceCollection) {
  const row = payload.new && Object.keys(payload.new).length ? payload.new : payload.old;
  if (!row) return;
  const col = forceCollection || row.collection;
  const b = bucket(memStore, rid);
  const arr = b[col]; if (!arr) return;
  const idx = arr.findIndex((d) => d.id === row.id);
  if (payload.eventType === "DELETE") { if (idx >= 0) arr.splice(idx, 1); }
  else if (row.data) { if (idx >= 0) arr[idx] = row.data; else arr.unshift(row.data); }
  emit();
}

// ---------- seed ----------
function seedRestaurant(b, rid) {
  const mk = (dept, title, frequency, items) => ({
    id: uid(), entity_type: "checklist", department: dept, title, frequency, status: "published",
    items: items.map((t) => ({ id: uid(), text: t.t || t, requires_photo: !!t.photo })), created_at: nowISO(), updated_at: nowISO(),
  });
  b.checklists = [
    mk("stad", "Daglig städning", "Dagligen", [{ t: "Toaletter rengjorda", photo: true }, "Golv matsal", "Ytor & bord", "Avfallshantering", { t: "Diskstation rengjord", photo: true }]),
    mk("stad", "Veckostäd", "Veckovis", ["Fläktfilter", "Kylar invändigt", "Bakom utrustning", "Golvbrunnar"]),
    mk("kok", "Öppning kök", "Dagligen", [{ t: "Kontrollera kyltemperaturer", photo: true }, "Starta utrustning", "Mise en place klar", "Kvittera leveranser"]),
    mk("servering", "Förberedelse service", "Per skift", ["Duka borden", "Kontrollera meny", "Genomgång dagens rätter", "Kontrollera bokningar"]),
  ];
  b.tasks = [
    { id: uid(), entity_type: "task", department: "stad", title: "Beställ rengöringsmedel", description: "Allrent och maskindisk tar slut.", status: "todo", priority: "medium", assignee: "Anna Lind", due_at: nowISO(), tags: ["inköp"], created_at: nowISO(), updated_at: nowISO(), comments: [], attachments: [] },
    { id: uid(), entity_type: "task", department: "stad", title: "Avkalka diskmaskin", description: "Månadsrutin.", status: "in_progress", priority: "high", assignee: "Marcus Holm", due_at: nowISO(), tags: ["underhåll"], created_at: nowISO(), updated_at: nowISO(), comments: [], attachments: [] },
  ];
  b.incidents = [
    { id: uid(), entity_type: "incident", department: "stad", title: "Diskmaskin läcker", description: "Vatten på golvet vid diskstation. Halkrisk.", severity: "high", status: "open", reporter: "Marcus Holm", tags: ["utrustning"], created_at: nowISO(), updated_at: nowISO(), comments: [], attachments: [] },
  ];
  b.manuals = [
    { id: uid(), entity_type: "manual", department: "stad", title: "Egenkontroll städ (HACCP)", category: "Säkerhet", status: "published", author: "Sara Berg", content: "Daglig: golv, toaletter, ytor. Veckovis: kylar, fläktfilter. Dokumentera varje moment med signatur i checklistan. Vid avvikelse: åtgärda och rapportera som problem.", created_at: nowISO(), updated_at: nowISO() },
    { id: uid(), entity_type: "manual", department: "kunskap", title: "Personalhandbok", category: "Policy", status: "published", author: "Drottningen", content: "Arbetstider, klädkod, sjukanmälan och kärnvärden. Vi bemöter varje gäst som om det vore hemma hos oss. Respekt, renlighet och punktlighet är grunden i allt vi gör.", created_at: nowISO(), updated_at: nowISO() },
    { id: uid(), entity_type: "manual", department: "kunskap", title: "Allergi- & specialkostpolicy", category: "Policy", status: "published", author: "Sara Berg", content: "Fråga alltid om allergier vid beställning. De 14 allergenerna ska kunna redovisas per rätt. Vid osäkerhet: fråga köket, gissa aldrig. Korskontaminering undviks med separata redskap.", created_at: nowISO(), updated_at: nowISO() },
    { id: uid(), entity_type: "manual", department: "kunskap", title: "Öppning & stängning – rutin", category: "Rutin", status: "published", author: "Marcus Holm", content: "Öppning: lås upp, larm av, tänd, kontrollera kyltemperaturer, starta utrustning. Stängning: kassaavstämning, rengöring, släck, larma på, lås.", created_at: nowISO(), updated_at: nowISO() },
    { id: uid(), entity_type: "manual", department: "kunskap", title: "Brand- & utrymningsrutin", category: "Säkerhet", status: "published", author: "Drottningen", content: "Vid brand: larma 112, utrym via närmaste utgång, samlingsplats på parkeringen. Brandsläckare finns vid kök och entré. Kontrolleras årligen.", created_at: nowISO(), updated_at: nowISO() },
  ];
  b.notifications = [
    { id: uid(), title: "Nytt problem rapporterat", body: "Diskmaskin läcker · Disk & Städ", event_type: "IncidentCreated", link: "/konto/operation/stad", tab: "problem", department: "stad", created_at: nowISO(), read: false },
    { id: uid(), title: "Checklistor kvar idag", body: "Kök har checklistor som inte är avbockade", event_type: "ChecklistDue", link: "/konto/operation/kok", tab: "checklistor", department: "kok", created_at: nowISO(), read: false },
  ];
  b.audit = [
    { id: uid(), action: "create", actor_name: "Marcus Holm", entity_type: "incident", department: "stad", created_at: nowISO() },
    { id: uid(), action: "create", actor_name: "Sara Berg", entity_type: "manual", department: "kunskap", created_at: nowISO() },
  ];
  // Foodcost seed: råvaror + ett recept
  const ing = (name, supplier, size, unit, price, par = 6) => ({
    id: uid(), entity_type: "ingredient", name, supplier, pack_size: size, pack_unit: unit, pack_price: price,
    base_unit: baseUnitOf(unit), price_per_base: pricePerBase(price, size, unit), price_history: [],
    par_level: par, stock_unit: "st", current_stock: null,
    created_at: nowISO(), updated_at: nowISO(),
  });
  const mozz = ing("Mozzarella", "Nordisk Ost AB", 2, "kg", 198, 15);
  const mjol = ing("Vetemjöl 00", "Kvarnen", 25, "kg", 320, 4);
  const tomat = ing("Krossade tomater", "Italia Import", 2.55, "kg", 42, 20);
  const olja = ing("Olivolja", "Italia Import", 5, "l", 480, 6);
  const basilika = ing("Basilika (kruka)", "Grönt & Skönt", 1, "st", 19, 10);
  b.ingredients = [mozz, mjol, tomat, olja, basilika];
  b.recipes = [
    { id: uid(), entity_type: "recipe", name: "Pizza Margherita", portions: 1, sale_price: 129, packaging_cost: 4, labor_cost: 12, foodcost_target: 30,
      items: [
        { id: uid(), ingredient_id: mjol.id, qty: 220, unit: "g" },
        { id: uid(), ingredient_id: tomat.id, qty: 80, unit: "g" },
        { id: uid(), ingredient_id: mozz.id, qty: 125, unit: "g" },
        { id: uid(), ingredient_id: olja.id, qty: 10, unit: "ml" },
        { id: uid(), ingredient_id: basilika.id, qty: 0.2, unit: "st" },
      ], created_at: nowISO(), updated_at: nowISO() },
  ];
  return b;
}

export function ensureSeed(rid) {
  if (IS_MOCK) {
    const db = read();
    if (!db[rid]) { const b = bucket(db, rid); if (rid === "rest-1") seedRestaurant(b, rid); write(db); }
    return;
  }
  if (!hydrated.has(rid)) hydrate(rid);
}

// ---------- audit + notifications ----------
function pushAudit(b, action, entity_type, actor, extra = {}) {
  b.audit.unshift({ id: uid(), action, entity_type, actor_name: actor || "System", created_at: nowISO(), ...extra });
}
// Owner can silence GENERAL notifications; person/department-directed ones always go through.
function notifEnabled(b) {
  const s = (b.settings || []).find((x) => x.key === "notifications");
  return s ? s.enabled !== false : true;
}
function notify(b, title, body, event_type, link, tab, meta = {}) {
  const { department = null, assignee = null } = meta;
  const directed = !!(department || assignee);
  if (!directed && !notifEnabled(b)) return; // general notification silenced by owner
  b.notifications.unshift({ id: uid(), title, body, event_type, link, tab, department, assignee, created_at: nowISO(), read: false });
}

// Owner notification on/off (whole restaurant).
export function getNotificationsEnabled(rid) {
  const s = list(rid, "settings").find((x) => x.key === "notifications");
  return s ? s.enabled !== false : true;
}
export function setNotificationsEnabled(rid, enabled, actor) {
  const existing = list(rid, "settings").find((x) => x.key === "notifications");
  return existing
    ? update(rid, "settings", existing.id, { enabled }, actor)
    : create(rid, "settings", { entity_type: "setting", key: "notifications", enabled }, actor);
}

// Scoped feeds: staff/shift-leaders (who have a department) only see their own; managers/owners (no department) see all.
export function activityFeed(rid, department = null, limit = 20) {
  let rows = list(rid, "audit");
  if (department) rows = rows.filter((e) => e.department === department);
  return rows.slice(0, limit);
}
export function notificationsFor(rid, department = null) {
  let rows = list(rid, "notifications");
  if (department) rows = rows.filter((n) => n.department === department);
  return rows;
}

// ---------- generic CRUD ----------
export function list(rid, collection, filter = {}) {
  const b = bucket(read(), rid);
  let rows = (b[collection] || []).filter((r) => !r.deleted_at);
  Object.entries(filter).forEach(([k, v]) => { if (v != null) rows = rows.filter((r) => r[k] === v); });
  return rows;
}
export function get(rid, collection, id) { return list(rid, collection).find((r) => r.id === id) || null; }

export function create(rid, collection, data, actor) {
  const db = read(); const b = bucket(db, rid);
  const doc = { id: uid(), created_at: nowISO(), updated_at: nowISO(), comments: [], attachments: [], ...data };
  b[collection].unshift(doc);
  pushAudit(b, "create", doc.entity_type || collection, actor, { department: doc.department });
  if (collection === "incidents") notify(b, "Nytt problem rapporterat", `${doc.title} · ${departmentBySlug(doc.department)?.name || ""}`, "IncidentCreated", `/konto/operation/${doc.department}`, "problem", { department: doc.department });
  if (collection === "tasks" && doc.assignee) notify(b, "Ny uppgift tilldelad", doc.title, "TaskAssigned", `/konto/operation/${doc.department}`, "uppgifter", { department: doc.department, assignee: doc.assignee });
  write(db);
  return doc;
}
export function update(rid, collection, id, updates, actor) {
  const db = read(); const b = bucket(db, rid);
  const row = b[collection].find((r) => r.id === id); if (!row) return null;
  const before = { ...row };
  // Version history (rule 6: everything saved) — snapshot manuals before each edit.
  if (collection === "manuals") {
    row.versions = row.versions || [];
    row.versions.unshift({
      id: uid(), version: row.versions.length + 1,
      title: before.title, category: before.category, content: before.content,
      author: before.author, saved_by: actor, saved_at: nowISO(),
    });
  }
  Object.assign(row, updates, { updated_at: nowISO() });
  const diff = {};
  Object.keys(updates).forEach((k) => { if (before[k] !== row[k] && typeof row[k] !== "object") diff[k] = { from: before[k], to: row[k] }; });
  pushAudit(b, "update", row.entity_type || collection, actor, { ...(Object.keys(diff).length ? { diff } : {}), department: row.department });
  if (collection === "incidents" && updates.status === "resolved") notify(b, "Problem löst", row.title, "IncidentResolved", `/konto/operation/${row.department}`, "problem", { department: row.department });
  write(db);
  return row;
}
export function remove(rid, collection, id, actor) {
  const db = read(); const b = bucket(db, rid);
  const row = b[collection].find((r) => r.id === id); if (!row) return;
  row.deleted_at = nowISO();
  pushAudit(b, "delete", row.entity_type || collection, actor, { department: row.department });
  write(db);
}

export function addComment(rid, collection, id, body, author) {
  const db = read(); const b = bucket(db, rid);
  const row = b[collection].find((r) => r.id === id); if (!row) return;
  (row.comments = row.comments || []).push({ id: uid(), author, body, created_at: nowISO() });
  pushAudit(b, "update", row.entity_type || collection, author, { diff: { kommentar: { from: "", to: body.slice(0, 40) } }, department: row.department });
  write(db);
}
export function addAttachment(rid, collection, id, att, actor) {
  const db = read(); const b = bucket(db, rid);
  const row = b[collection].find((r) => r.id === id); if (!row) return;
  (row.attachments = row.attachments || []).push({ id: uid(), ai_tags: att.ai_tags || [], ...att });
  pushAudit(b, "update", row.entity_type || collection, actor, { diff: { bilaga: { from: "", to: att.file_name } }, department: row.department });
  write(db);
}

export function saveChecklistRun(rid, checklistId, checkedItemIds, actor) {
  const db = read(); const b = bucket(db, rid);
  const cl = b.checklists.find((c) => c.id === checklistId); if (!cl) return;
  const total = cl.items.length, done = checkedItemIds.length;
  const date = todayISO();
  const existing = b.checklist_runs.find((r) => r.checklist_id === checklistId && r.date === date);
  const run = { checklist_id: checklistId, department: cl.department, date, total, done, complete: done >= total, checked: checkedItemIds, completed_by: actor, updated_at: nowISO() };
  if (existing) Object.assign(existing, run); else b.checklist_runs.unshift({ id: uid(), ...run });
  pushAudit(b, "complete", "checklist", actor, { diff: { framsteg: { from: "", to: `${done}/${total}` } }, department: cl.department });
  if (done >= total) notify(b, "Checklista slutförd", cl.title, "ChecklistCompleted", `/konto/operation/${cl.department}`, "checklistor", { department: cl.department });
  write(db);
}
export function todayRun(rid, checklistId) {
  return list(rid, "checklist_runs").find((r) => r.checklist_id === checklistId && r.date === todayISO()) || null;
}

// Per-department checklist completion for today: how many are not fully checked off.
export function deptChecklistStatus(rid, slug) {
  const cls = list(rid, "checklists").filter((c) => c.department === slug);
  let open = 0;
  cls.forEach((c) => {
    const items = c.items || [];
    const run = todayRun(rid, c.id);
    const done = run ? items.filter((it) => (run.checked || []).includes(it.id)).length : 0;
    if (!run || done < items.length) open += 1;
  });
  return { total: cls.length, open };
}

// Aggregated open work items for today across departments (checklists, incidents, tasks).
export function openChecklistsToday(rid) {
  return list(rid, "checklists").map((c) => {
    const items = c.items || [];
    const run = todayRun(rid, c.id);
    const done = run ? items.filter((it) => (run.checked || []).includes(it.id)).length : 0;
    return { ...c, done, total: items.length, complete: items.length > 0 && done >= items.length };
  }).filter((c) => !c.complete);
}

export function todayTodos(rid) {
  const items = [];
  openChecklistsToday(rid).forEach((c) => items.push({ id: `cl-${c.id}`, kind: "checklist", title: c.title, department: c.department, meta: `${c.done}/${c.total} avbockade` }));
  list(rid, "incidents").filter((i) => i.status !== "closed" && i.status !== "resolved").forEach((i) => items.push({ id: `in-${i.id}`, kind: "incident", title: i.title, department: i.department, meta: `Problem${i.severity ? ` · ${i.severity}` : ""}` }));
  list(rid, "tasks").filter((t) => t.status !== "done").forEach((t) => items.push({ id: `tk-${t.id}`, kind: "task", title: t.title, department: t.department, meta: t.assignee ? `Uppgift · ${t.assignee}` : "Uppgift" }));
  return items;
}

// Owner control summary across all departments (today): approved / failed(partial) / missed.
export function controlSummary(rid) {
  const cls = list(rid, "checklists");
  let approved = 0, failed = 0, missed = 0;
  cls.forEach((c) => {
    const items = c.items || [];
    const run = todayRun(rid, c.id);
    if (!run) { missed += 1; return; }
    const done = items.filter((it) => (run.checked || []).includes(it.id)).length;
    if (items.length > 0 && done >= items.length) approved += 1;
    else failed += 1;
  });
  const total = cls.length;
  const pctOf = (n) => (total ? Math.round((n / total) * 100) : 0);
  return { total, approved, failed, missed, approvedPct: pctOf(approved), failedPct: pctOf(failed), missedPct: pctOf(missed) };
}

// Per operational department: today's checklist completion (fully done = "godkänd").
export function deptControlSummaries(rid) {
  return DEPARTMENTS
    .filter((d) => !KNOWLEDGE_SLUGS.includes(d.slug) && !OWNER_ONLY_SLUGS.includes(d.slug))
    .map((d) => {
      const cls = list(rid, "checklists").filter((c) => c.department === d.slug);
      let done = 0;
      cls.forEach((c) => {
        const items = c.items || [];
        const run = todayRun(rid, c.id);
        const n = run ? items.filter((it) => (run.checked || []).includes(it.id)).length : 0;
        if (items.length > 0 && n >= items.length) done += 1;
      });
      return { slug: d.slug, name: d.name, color: d.color, done, total: cls.length };
    })
    .filter((d) => d.total > 0);
}

// Overall restaurant status % (checklist item completion today) + remaining lists + last week's daily average.
export function restaurantStatus(rid) {
  const cls = list(rid, "checklists");
  let doneItems = 0, totalItems = 0, openLists = 0;
  cls.forEach((c) => {
    const items = c.items || [];
    const run = todayRun(rid, c.id);
    const n = run ? items.filter((it) => (run.checked || []).includes(it.id)).length : 0;
    doneItems += n; totalItems += items.length;
    if (items.length > 0 && n < items.length) openLists += 1;
  });
  const now = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;
  const today = todayISO();
  const byDate = {};
  list(rid, "checklist_runs").forEach((r) => {
    if (r.date === today) return;
    (byDate[r.date] = byDate[r.date] || { done: 0, total: 0 });
    byDate[r.date].done += r.done || 0; byDate[r.date].total += r.total || 0;
  });
  const dates = Object.keys(byDate).sort().slice(-7);
  const lastWeekAvg = dates.length
    ? Math.round(dates.reduce((s, d) => s + (byDate[d].total ? byDate[d].done / byDate[d].total : 0), 0) / dates.length * 100)
    : null;
  return { now, remaining: openLists, lastWeekAvg };
}

// Manual figures until Qopla/Google/POS integrations are connected.
export function getReviews(rid) {
  return list(rid, "settings").find((x) => x.key === "reviews") || null;
}
export function setReviews(rid, data, actor) {
  const ex = list(rid, "settings").find((x) => x.key === "reviews");
  return ex ? update(rid, "settings", ex.id, data, actor)
            : create(rid, "settings", { entity_type: "setting", key: "reviews", ...data }, actor);
}
export function getSalesMix(rid) {
  const s = list(rid, "settings").find((x) => x.key === "sales_mix");
  return s && s.items && s.items.length ? s : null;
}
export function setSalesMix(rid, items, actor) {
  const ex = list(rid, "settings").find((x) => x.key === "sales_mix");
  return ex ? update(rid, "settings", ex.id, { items }, actor)
            : create(rid, "settings", { entity_type: "setting", key: "sales_mix", items }, actor);
}

// Restore a manual to a previous version. Snapshots the current state first, so
// nothing is ever lost (rule 6), then applies the chosen version's fields.
export function restoreManualVersion(rid, id, versionId, actor) {
  const db = read(); const b = bucket(db, rid);
  const row = b.manuals.find((r) => r.id === id); if (!row) return;
  const v = (row.versions || []).find((x) => x.id === versionId); if (!v) return;
  row.versions = row.versions || [];
  row.versions.unshift({
    id: uid(), version: row.versions.length + 1,
    title: row.title, category: row.category, content: row.content,
    author: row.author, saved_by: actor, saved_at: nowISO(),
  });
  row.title = v.title; row.category = v.category; row.content = v.content; row.updated_at = nowISO();
  pushAudit(b, "restore", "manual", actor, { diff: { version: { from: "", to: `v${v.version}` } } });
  write(db);
}

export function markNotificationRead(rid, id) {
  const db = read(); const b = bucket(db, rid);
  const n = b.notifications.find((x) => x.id === id); if (n) n.read = true; write(db);
}

export function search(rid, q) {
  if (!q || q.length < 1) return {};
  const ql = q.toLowerCase();
  const groups = {};
  const map = { checklists: "checklist", tasks: "task", incidents: "incident", manuals: "manual" };
  Object.entries(map).forEach(([col, type]) => {
    const hits = list(rid, col).filter((r) => (r.title || "").toLowerCase().includes(ql))
      .map((r) => ({ ref_id: r.id, title: r.title, status: r.status, department: r.department }));
    if (hits.length) groups[type] = hits;
  });
  return groups;
}

// Grounding context for the AI Assistant ("Assistenten") — the restaurant's own
// manuals, recipes (with resolved ingredient grams) and open problems.
export function assistantContext(rid) {
  const ings = list(rid, "ingredients");
  const byId = Object.fromEntries(ings.map((i) => [i.id, i]));
  const manuals = list(rid, "manuals").map((m) => ({
    id: m.id, title: m.title, category: m.category, department: m.department, content: m.content || "",
  }));
  const recipes = list(rid, "recipes").map((r) => ({
    id: r.id, name: r.name, portions: r.portions,
    ingredients: (r.items || []).map((it) => ({ name: byId[it.ingredient_id]?.name || "(borttagen)", qty: it.qty, unit: it.unit })),
  }));
  const problems = list(rid, "incidents")
    .filter((i) => i.status !== "closed" && i.status !== "resolved")
    .map((i) => ({ id: i.id, title: i.title, department: i.department, status: i.status }));
  return { manuals, recipes, problems };
}


export function stats(rid) {
  const incidents = list(rid, "incidents");
  const open = incidents.filter((i) => i.status !== "closed" && i.status !== "resolved").length;
  const checklists = list(rid, "checklists");
  const runs = list(rid, "checklist_runs").filter((r) => r.date === todayISO());
  const completion = runs.length ? Math.round(runs.reduce((a, r) => a + r.done, 0) / Math.max(runs.reduce((a, r) => a + r.total, 0), 1) * 100) : 0;
  return {
    open_incidents: open, resolved: incidents.length - open,
    tasks: list(rid, "tasks").length, manuals: list(rid, "manuals").length,
    checklists: checklists.length, today_completion: completion,
  };
}

// ---------- foodcost ----------
export function upsertIngredient(rid, data, actor) {
  const db = read(); const b = bucket(db, rid);
  b.ingredients = b.ingredients || [];
  const norm = (s) => (s || "").trim().toLowerCase();
  const ppb = pricePerBase(data.pack_price, data.pack_size, data.pack_unit);
  let ing = data.id ? b.ingredients.find((x) => x.id === data.id) : b.ingredients.find((x) => norm(x.name) === norm(data.name));
  if (ing) {
    const oldPrice = ing.pack_price;
    const np = Number(data.pack_price);
    const priceChanged = oldPrice != null && (oldPrice !== np || ing.pack_size !== Number(data.pack_size) || ing.pack_unit !== data.pack_unit);
    if (priceChanged) {
      ing.price_history = ing.price_history || [];
      ing.price_history.unshift({
        id: uid(), date: nowISO(), old_price: oldPrice, new_price: np,
        change_kr: +(np - oldPrice).toFixed(2), change_pct: oldPrice ? +(((np - oldPrice) / oldPrice) * 100).toFixed(1) : 0,
      });
    }
    Object.assign(ing, {
      name: (data.name || ing.name).trim(), supplier: data.supplier != null ? data.supplier : ing.supplier,
      pack_size: Number(data.pack_size), pack_unit: data.pack_unit, pack_price: np,
      base_unit: baseUnitOf(data.pack_unit), price_per_base: ppb, updated_at: nowISO(),
      par_level: data.par_level != null && data.par_level !== "" ? Number(data.par_level) : (ing.par_level || 0),
      stock_unit: data.stock_unit != null ? data.stock_unit : (ing.stock_unit || "st"),
      product_type: data.product_type != null ? data.product_type : (ing.product_type || "ravara"),
    });
    pushAudit(b, "update", "ingredient", actor, { diff: { pris: { from: `${oldPrice} kr`, to: `${np} kr` } } });
    if (priceChanged) {
      const affected = (b.recipes || []).filter((r) => (r.items || []).some((it) => it.ingredient_id === ing.id)).map((r) => r.id);
      b.price_impacts = b.price_impacts || [];
      b.price_impacts.unshift({ id: uid(), ingredient_id: ing.id, ingredient_name: ing.name, old_price: oldPrice, new_price: np, unit: ing.pack_unit, affected_recipe_ids: affected, resolved: false, created_at: nowISO() });
      const changePct = oldPrice ? ((np - oldPrice) / oldPrice) * 100 : 0;
      if (Math.abs(changePct) >= 5) {
        const dir = changePct > 0 ? "höjts" : "sänkts";
        notify(b, `Prisavvikelse: ${ing.name}`, `${ing.name} har ${dir} ${Math.abs(changePct).toFixed(1)}% (${oldPrice} kr → ${np} kr per förpackning). ${affected.length} recept påverkas.`, "PriceDeviation", "/konto/foodcost");
        pushAudit(b, "flag", "ingredient", actor, { note: `Prisavvikelse ${changePct > 0 ? "+" : ""}${changePct.toFixed(1)}% på ${ing.name}` });
      }
    }
  } else {
    ing = {
      id: uid(), entity_type: "ingredient", name: (data.name || "").trim(), supplier: data.supplier || "",
      pack_size: Number(data.pack_size), pack_unit: data.pack_unit, pack_price: Number(data.pack_price),
      base_unit: baseUnitOf(data.pack_unit), price_per_base: ppb, price_history: [],
      par_level: data.par_level != null && data.par_level !== "" ? Number(data.par_level) : 0,
      stock_unit: data.stock_unit || "st",
      product_type: data.product_type || "ravara",
      current_stock: data.current_stock != null && data.current_stock !== "" ? Number(data.current_stock) : null,
      created_at: nowISO(), updated_at: nowISO(),
    };
    b.ingredients.unshift(ing);
    pushAudit(b, "create", "ingredient", actor);
  }
  write(db);
  return ing;
}

// Jämför en avläst fakturarad mot inlagda produkter (matchar på namn).
// status: "ny" (finns ej) | "prisandring" (finns, pris avviker > tröskel) | "kand" (finns, ~samma pris)
export function ingredientMatchStatus(rid, line, thresholdPct = 5) {
  const db = read(); const b = bucket(db, rid);
  const norm = (s) => (s || "").trim().toLowerCase();
  if (!norm(line.name)) return { status: "ny", oldPrice: null, changePct: null };
  const ing = (b.ingredients || []).find((x) => norm(x.name) === norm(line.name));
  if (!ing) return { status: "ny", oldPrice: null, changePct: null };
  const newPpb = pricePerBase(line.pack_price, line.pack_size, line.pack_unit);
  const oldPpb = ing.price_per_base != null ? ing.price_per_base : pricePerBase(ing.pack_price, ing.pack_size, ing.pack_unit);
  const changePct = oldPpb ? ((newPpb - oldPpb) / oldPpb) * 100 : 0;
  const rounded = +changePct.toFixed(1);
  if (Math.abs(changePct) > thresholdPct) return { status: "prisandring", oldPrice: ing.pack_price, changePct: rounded };
  return { status: "kand", oldPrice: ing.pack_price, changePct: rounded };
}


// ---------- lager / produkter ----------
// Save a staff stock count. Updates current_stock on products (shared with
// foodcost ingredients) so the owner sees "aktuellt lager" live.
export function saveStockCount(rid, counts, actor) {
  const db = read(); const b = bucket(db, rid);
  let n = 0;
  Object.entries(counts).forEach(([id, val]) => {
    if (val === "" || val == null) return;
    const ing = (b.ingredients || []).find((x) => x.id === id);
    if (ing) { ing.current_stock = Number(val); ing.stock_counted_at = nowISO(); ing.updated_at = nowISO(); n += 1; }
  });
  if (n) {
    pushAudit(b, "count", "ingredient", actor, { diff: { inventering: { from: "", to: `${n} produkter räknade` } } });
    notify(b, "Inventering sparad", `${n} produkter uppdaterade i lagret`, "StockCounted", "/konto/operation/lager", "produkter", { department: "lager" });
    const under = (b.ingredients || []).filter((i) => (i.par_level || 0) > 0 && (i.current_stock ?? 0) < i.par_level).length;
    if (under > 0) notify(b, "Produkter under par-nivå", `${under} produkter behöver beställas`, "LowStock", "/konto/operation/lager", "bestallning", { department: "lager" });
  }
  write(db);
  return n;
}

// Persist a custom product order (within a supplier group). orderedIds = ids in desired order.
export function reorderProducts(rid, orderedIds, actor) {
  const db = read(); const b = bucket(db, rid);
  orderedIds.forEach((id, i) => {
    const ing = (b.ingredients || []).find((x) => x.id === id);
    if (ing) { ing.sort_order = i * 10; ing.updated_at = nowISO(); }
  });
  write(db);
}

// Lightweight update of lager config (par-nivå / lagerenhet / typ) without
// touching price fields — used for inline editing in the Produkter list.
export function setProductStockConfig(rid, id, patch, actor) {
  const db = read(); const b = bucket(db, rid);
  const ing = (b.ingredients || []).find((x) => x.id === id); if (!ing) return null;
  if (patch.par_level !== undefined) ing.par_level = patch.par_level === "" ? 0 : Number(patch.par_level);
  if (patch.stock_unit !== undefined) ing.stock_unit = patch.stock_unit;
  if (patch.product_type !== undefined) ing.product_type = patch.product_type;
  ing.updated_at = nowISO();
  pushAudit(b, "update", "ingredient", actor, { diff: { lager: { from: "", to: id } } });
  write(db);
  return ing;
}

// Products grouped by supplier (leverantör). Custom order via `sort_order`, else A–Z.
export function productsBySupplier(rid) {
  const items = list(rid, "ingredients");
  const groups = {};
  items.forEach((i) => { const s = (i.supplier || "").trim() || "Övrigt"; (groups[s] = groups[s] || []).push(i); });
  const byOrder = (a, z) => {
    const ao = a.sort_order, zo = z.sort_order;
    if (ao != null && zo != null && ao !== zo) return ao - zo;
    if (ao != null && zo == null) return -1;
    if (ao == null && zo != null) return 1;
    return (a.name || "").localeCompare(z.name || "");
  };
  return Object.entries(groups)
    .map(([supplier, arr]) => ({ supplier, items: arr.slice().sort(byOrder) }))
    .sort((a, z) => a.supplier.localeCompare(z.supplier));
}

// Owner summary for the Lager shortcut card.
export function lagerSummary(rid) {
  const items = list(rid, "ingredients");
  const underPar = items.filter((i) => (i.par_level || 0) > 0 && (i.current_stock ?? 0) < i.par_level).length;
  const suppliers = new Set(items.map((i) => (i.supplier || "").trim() || "Övrigt")).size;
  return { total: items.length, underPar, suppliers };
}

// One-click order proposal: everything under par-nivå, grouped by supplier.
export function orderSuggestions(rid) {
  return productsBySupplier(rid).map((g) => ({
    supplier: g.supplier,
    items: g.items.map((p) => {
      const cur = p.current_stock ?? 0;
      return { id: p.id, name: p.name, par: p.par_level || 0, current: p.current_stock, order: Math.max((Number(p.par_level) || 0) - cur, 0), stock_unit: p.stock_unit || "st" };
    }).filter((x) => x.order > 0),
  })).filter((g) => g.items.length);
}

// Most recent inventory count timestamp across products (for the reminder).
export function lastStockCount(rid) {
  let latest = null;
  list(rid, "ingredients").forEach((i) => { if (i.stock_counted_at && (!latest || i.stock_counted_at > latest)) latest = i.stock_counted_at; });
  return latest;
}

// Configurable inventory-reminder interval (days), per restaurant.
export function getLagerReminderDays(rid) {
  const s = list(rid, "settings").find((x) => x.key === "lager");
  return s?.reminder_days ?? 7;
}
export function setLagerReminderDays(rid, days, actor) {
  const val = Math.max(1, Number(days) || 7);
  const existing = list(rid, "settings").find((x) => x.key === "lager");
  return existing
    ? update(rid, "settings", existing.id, { reminder_days: val }, actor)
    : create(rid, "settings", { entity_type: "setting", key: "lager", reminder_days: val }, actor);
}

export function approveInvoice(rid, invoice, actor) {
  (invoice.lines || []).forEach((ln) => upsertIngredient(rid, ln, actor));
  const db = read(); const b = bucket(db, rid);
  b.invoices = b.invoices || [];
  const lines = (invoice.lines || []).map((l) => ({
    name: l.name || "", supplier: l.supplier || "", qty: Number(l.qty) || 0,
    pack_size: Number(l.pack_size) || 0, pack_unit: l.pack_unit || "st", pack_price: Number(l.pack_price) || 0,
  }));
  const total = +lines.reduce((a, l) => a + l.pack_price * (l.qty || 1), 0).toFixed(2);
  b.invoices.unshift({ id: uid(), supplier: invoice.supplier || "", file_name: invoice.file_name || "",
    photo_bucket: invoice.photo_bucket || null, photo_path: invoice.photo_path || null,
    lines, total, line_count: lines.length, status: "approved", created_by: actor, created_at: nowISO() });
  pushAudit(b, "create", "invoice", actor);
  notify(b, "Faktura godkänd", `${lines.length} råvarupriser uppdaterade`, "InvoiceApproved", "/konto/foodcost");
  write(db);
}

export function computeRecipe(rid, recipe) {
  const ings = list(rid, "ingredients");
  const byId = Object.fromEntries(ings.map((i) => [i.id, i]));
  const lines = (recipe.items || []).map((it) => {
    const ing = byId[it.ingredient_id];
    const cost = ing ? toBase(it.qty, it.unit) * ing.price_per_base : 0;
    return { ...it, name: ing?.name || "(borttagen råvara)", supplier: ing?.supplier, missing: !ing, cost: +cost.toFixed(2) };
  });
  const ingredientCost = +lines.reduce((a, l) => a + l.cost, 0).toFixed(2);
  const packaging = Number(recipe.packaging_cost) || 0;
  const labor = Number(recipe.labor_cost) || 0;
  const totalCost = +(ingredientCost + packaging + labor).toFixed(2);
  const price = Number(recipe.sale_price) || 0;
  const grossKr = +(price - totalCost).toFixed(2);
  const grossPct = price > 0 ? +((grossKr / price) * 100).toFixed(1) : 0;
  const foodCostPct = price > 0 ? +((ingredientCost / price) * 100).toFixed(1) : 0;
  const portions = Number(recipe.portions) || 1;
  return { lines, ingredientCost, packaging, labor, totalCost, price, grossKr, grossPct, foodCostPct,
    costPerPortion: +(totalCost / portions).toFixed(2), portions, target: Number(recipe.foodcost_target) || 0 };
}

export function resolvePriceImpacts(rid, actor) {
  const db = read(); const b = bucket(db, rid);
  const pending = (b.price_impacts || []).filter((p) => !p.resolved);
  pending.forEach((p) => { p.resolved = true; p.resolved_at = nowISO(); });
  if (pending.length) pushAudit(b, "recompute", "recipe", actor, { diff: { recept: { from: "", to: `${pending.length} prisändringar granskade` } } });
  write(db);
  return pending.length;
}
